import { createRouter, createWebHistory } from 'vue-router'
import MainLayout from '@/components/templates/MainLayout.vue'
import { useAuthStore } from '@/stores/auth.store'
import type { AccessLevel } from '@/types/auth.types'

// Convenience aliases (extend as you add role-gated routes).
// 2-level model (2026-05-27): only ADMIN and USER. The previous MANAGER tier
// has been removed. Add new gated aliases here as needed (e.g. ADMIN_ONLY).
const ALL: AccessLevel[] = ['access_level.admin', 'access_level.user']
const ADMIN_ONLY: AccessLevel[] = ['access_level.admin']
// Re-export so future routes can import without redeclaring
export { ALL, ADMIN_ONLY }

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Auth routes (outside MainLayout)
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/LoginView.vue'),
      meta: { public: true },
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('@/views/auth/AuthCallbackView.vue'),
      meta: { public: true },
    },
    {
      path: '/',
      component: MainLayout,
      children: [
        {
          // Metrics shell (2026-10-29): one sidebar entry, one tab per measured
          // system. The KB Assistant tab is admin-only.
          path: '',
          component: () => import('@/views/metrics/MetricsView.vue'),
          meta: { access_levels: ALL },
          children: [
            {
              path: '',
              name: 'metrics',
              component: () => import('@/views/Dashboard.vue'),
              meta: { access_levels: ALL },
            },
            {
              // KB Assistant statistics (contract api-contract-kb-assistant-stats.md).
              path: 'metrics/kb-assistant',
              name: 'kb-assistant-stats',
              component: () => import('@/views/kbAssistant/KbAssistantStatsView.vue'),
              meta: { access_levels: ADMIN_ONLY },
            },
          ],
        },
        {
          path: '/account',
          name: 'account',
          component: () => import('@/views/Account.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/users',
          name: 'users-list',
          component: () => import('@/views/users/UsersListView.vue'),
          meta: { access_levels: ADMIN_ONLY },
        },
        // KB list views were removed from the UI 2026-06-09 — the
        // operator always works on the current draft via /kb-editor.
        // We keep the paths as redirects so any bookmark / audit link
        // lands on the right place instead of 404'ing.
        {
          path: '/kb-versions',
          name: 'kb-version-list',
          redirect: { name: 'kb-editor-entry' },
        },
        {
          path: '/kb-topics',
          name: 'kb-topic-list',
          redirect: { name: 'kb-editor-entry' },
        },
        {
          path: '/knowledge-base',
          name: 'knowledge-base-list',
          redirect: { name: 'kb-editor-entry' },
        },
        {
          // Retired 2026-06-16: the old /conversations (Review + CSAT KO tabs) is
          // superseded by the consolidated /session-review "Conversations review"
          // (CSAT is now a column + filter there). Redirect retires it gracefully;
          // the old view components remain on disk, unrouted, pending removal.
          path: '/conversations',
          redirect: { name: 'session-review-list' },
        },
        {
          path: '/conversations/:rest(.*)*',
          redirect: { name: 'session-review-list' },
        },
        {
          path: '/session-review',
          name: 'session-review-list',
          component: () => import('@/views/sessionReview/SessionReviewListView.vue'),
          meta: { access_levels: ADMIN_ONLY },
        },
        // Sidebar entry-points (frontend-shell plan, 2026-06-04). Each one
        // resolves to the matching deep-link route (KB editor / sandbox)
        // or renders an empty state when the resolver returns nothing.
        {
          // Per-paragraph-versioning model (2026-06): one global KB, no
          // version selection. The sidebar entry routes straight to the
          // editor — no draft-resolution hop. (The old KbEditorEntryView +
          // /kb-version/:id/edit route, plus the whole-KB version-workflow
          // surface — detail / sandbox / promote wizard / approval — were
          // removed with the backend kb_version workflow endpoints.)
          path: '/kb-editor',
          name: 'kb-editor-entry',
          component: () => import('@/views/kbEditor/KbEditorView.vue'),
          meta: { access_levels: ALL },
        },
        {
          // **Escalations, flat** (2026-09-14, user decision). It used to be a
          // tabbed shell — "Riepilogo" plus "Escalations" — and the summary tab
          // is gone: the user's rule is that the UI must make the situation
          // obvious without a page that narrates it, so the counts moved into
          // the rows and the columns of the table itself.
          //
          // The name stays **`kb-assistant`**. `config/navigation.ts` links by
          // route NAME, and so does the sidebar badge; renaming it would have
          // been a rename of the URL *and* of the contract every existing link
          // depends on, for no user-visible gain. The path is what changed.
          path: '/escalations',
          name: 'kb-assistant',
          component: () => import('@/views/kbAssistant/EscalationsView.vue'),
          meta: { access_levels: ALL },
        },
        {
          // Both old paths, kept as redirects — the same treatment the retired
          // routes above get (`/kb-versions`, `/kb-topics`, `/knowledge-base`).
          //
          // `/kb-assistant/escalations` in particular is load-bearing: it is
          // where a proposal's deep link points, which the 2026-09-10 UX review
          // calls the best thing in the product. A 404 there would break the one
          // path from an AI suggestion back to the support cases behind it.
          //
          // `query: (q) => q` is NOT decoration: the deep link carries
          // `?case=…` (repeatable, up to 500 refs) and vue-router drops the
          // query on a plain string redirect. Without it every deep link would
          // land on an unfiltered table.
          path: '/kb-assistant',
          redirect: (to) => ({ name: 'kb-assistant', query: to.query }),
        },
        {
          path: '/kb-assistant/escalations',
          redirect: (to) => ({ name: 'kb-assistant', query: to.query }),
        },
        {
          // Old address of the KB Assistant statistics, now a Metrics tab.
          path: '/kb-assistant-stats',
          redirect: { name: 'kb-assistant-stats' },
        },
        {
          path: '/test-staging',
          name: 'test-staging',
          component: () => import('@/views/testStaging/TestStagingView.vue'),
          meta: { access_levels: ALL },
        },
        {
          path: '/evaluation',
          component: () => import('@/views/evaluation/EvaluationView.vue'),
          meta: { access_levels: ALL },
          children: [
            {
              path: '',
              name: 'evaluation',
              redirect: { name: 'evaluation-catalog' },
            },
            {
              path: 'catalog',
              name: 'evaluation-catalog',
              component: () => import('@/views/evaluation/CatalogView.vue'),
              meta: { access_levels: ALL },
            },
            {
              path: 'runs',
              name: 'evaluation-runs',
              component: () => import('@/views/evaluation/RunsView.vue'),
              meta: { access_levels: ALL },
            },
            {
              // CQ validation (re-segmentation) — ADMIN ONLY, reads included.
              // Reversed on 2026-08-13: the surface is an operator console, not a
              // report. Every screen in it exists to support a decision only an
              // admin can take, and a proposed re-segmentation shown to someone
              // who cannot act on it reads as a change already made. The backend
              // gates the same three GETs, so this is not cosmetic; the tab is
              // also hidden from the sub-nav in EvaluationView.
              path: 'validation',
              name: 'evaluation-validation',
              component: () => import('@/views/evaluation/ValidationView.vue'),
              meta: { access_levels: ADMIN_ONLY },
            },
            {
              path: 'validation/:runId',
              name: 'evaluation-validation-detail',
              component: () => import('@/views/evaluation/ValidationRunDetailView.vue'),
              meta: { access_levels: ADMIN_ONLY },
            },
          ],
        },
      ],
    },
  ],
})

// Navigation guard — redirect to login if not authenticated, check role access
let authInitialized = false

/** Called from main.ts after auth.init() completes */
export function markAuthInitialized() {
  authInitialized = true
}

router.beforeEach(async (to) => {
  if (to.meta.public) return true

  const authStore = useAuthStore()

  // On first navigation (page load), auth might not be initialized yet.
  // Wait for it — init() is called in main.ts before mount, but the
  // router may start resolving before mount completes.
  if (!authInitialized) {
    try {
      await authStore.init()
    } catch {
      // init failed — will redirect to login below
    }
    authInitialized = true
  }

  if (!authStore.isAuthenticated) {
    return { name: 'login' }
  }

  // Role-based access check
  const requiredRoles = to.meta.access_levels as AccessLevel[] | undefined
  if (requiredRoles && authStore.currentUser) {
    if (!requiredRoles.includes(authStore.currentUser.access_level)) {
      return { name: 'metrics' }
    }
  }

  return true
})

export default router
